if (!customElements.get('history-timeline')) {
  customElements.define(
    'history-timeline',
    class HistoryTimeline extends HTMLElement {
      connectedCallback() {
        this.items = Array.from(this.querySelectorAll('.htl__item'));
        this.buttons = Array.from(this.querySelectorAll('.htl__year-btn'));
        this.years = this.querySelector('.htl__years');
        this.prev = this.querySelector('.htl__arrow--prev');
        this.next = this.querySelector('.htl__arrow--next');
        this.activeIndex = -1;
        this.ticking = false;
        this.locked = false;

        this.buttons.forEach((button, index) => button.addEventListener('click', () => this.goTo(index)));
        if (this.prev) this.prev.addEventListener('click', () => this.goTo(Math.max(0, this.activeIndex - 1)));
        if (this.next) {
          this.next.addEventListener('click', () => this.goTo(Math.min(this.items.length - 1, this.activeIndex + 1)));
        }

        this.onScroll = () => {
          if (this.ticking) return;
          this.ticking = true;
          requestAnimationFrame(() => {
            if (!this.locked) this.updateActive();
            this.ticking = false;
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll);
        window.addEventListener('load', this.onScroll);

        // In the theme editor, slide to an item when its block is selected.
        this.addEventListener('shopify:block:select', (event) => {
          const index = this.items.indexOf(event.target.closest('.htl__item'));
          if (index > -1) this.goTo(index);
        });

        this.updateActive();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        window.removeEventListener('load', this.onScroll);
      }

      // Height of a sticky/fixed header, so the item is not hidden underneath it.
      stickyHeaderHeight() {
        if (this.dataset.stickyHeader !== 'true') return 0;
        const header = document.querySelector('sticky-header') || document.querySelector('.section-header');
        if (!header) return 0;
        const position = getComputedStyle(header).position;
        return position === 'sticky' || position === 'fixed' ? header.offsetHeight : 0;
      }

      goTo(index) {
        const item = this.items[index];
        if (!item) return;

        const offset = (parseInt(this.dataset.offset, 10) || 0) + this.stickyHeaderHeight();
        const top = item.getBoundingClientRect().top + window.scrollY - offset;
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const smooth = this.dataset.smooth === 'true' && !reducedMotion;

        this.setActive(index);

        // While the page slides to the item, keep the chosen year highlighted instead of flickering through the years in between.
        this.locked = smooth;
        clearTimeout(this.unlockTimer);
        if (smooth) {
          const unlock = () => {
            this.locked = false;
            window.removeEventListener('scrollend', unlock);
            this.updateActive();
          };
          window.addEventListener('scrollend', unlock, { once: true });
          this.unlockTimer = setTimeout(unlock, 1500);
        }

        window.scrollTo({ top: Math.max(0, top), behavior: smooth ? 'smooth' : 'auto' });
      }

      // The active item is the one crossing the middle of the screen.
      updateActive() {
        const line = window.innerHeight / 2;
        let index = this.items.findIndex((item) => {
          const rect = item.getBoundingClientRect();
          return rect.top <= line && rect.bottom > line;
        });

        if (index === -1) {
          const first = this.items[0] && this.items[0].getBoundingClientRect();
          index = first && first.top > line ? 0 : this.items.length - 1;
        }
        this.setActive(index);
      }

      setActive(index) {
        if (index === this.activeIndex) return;
        this.activeIndex = index;

        this.buttons.forEach((button, buttonIndex) => {
          button.setAttribute('aria-current', String(buttonIndex === index));
        });

        if (this.prev) this.prev.disabled = index <= 0;
        if (this.next) this.next.disabled = index >= this.items.length - 1;

        // Keep the active year in view inside the horizontally scrolling navigation.
        const button = this.buttons[index];
        if (button && this.years) {
          const left = button.offsetLeft - (this.years.clientWidth - button.offsetWidth) / 2;
          this.years.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
        }
      }
    }
  );
}
